type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  data?: any;
  userId?: string;
  context?: string;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private logs: LogEntry[] = [];
  private maxLogs = 100;

  private createEntry(level: LogLevel, message: string, data?: any, context?: string): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      data,
      context,
    };
  }

  private addLog(entry: LogEntry) {
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  info(message: string, data?: any, context?: string) {
    const entry = this.createEntry('info', message, data, context);
    this.addLog(entry);
    if (this.isDevelopment) {
      console.log(`[INFO] ${message}`, data || '');
    }
  }

  warn(message: string, data?: any, context?: string) {
    const entry = this.createEntry('warn', message, data, context);
    this.addLog(entry);
    if (this.isDevelopment) {
      console.warn(`[WARN] ${message}`, data || '');
    }
  }

  error(message: string, error?: any, context?: string) {
    const entry = this.createEntry('error', message, error, context);
    this.addLog(entry);
    
    // Always log errors, even in production
    console.error(`[ERROR] ${message}`, error || '');
  }

  debug(message: string, data?: any, context?: string) {
    if (this.isDevelopment) {
      const entry = this.createEntry('debug', message, data, context);
      this.addLog(entry);
      console.debug(`[DEBUG] ${message}`, data || '');
    }
  }

  getLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

export const logger = new Logger();
